import { useQuery } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import { isMultilangPocEnabled } from '../../../../../constants/featureFlags'
import { useAnalyticsFeatureFlag } from '../../../../../contexts/AnalyticsFeatureFlagContext'
import ResultsOptionsControl from '../../../../../controls/ResultsOptions'
import { SurveyState } from '../../../../../data/gql-gen/dashboard/graphql'
import { LoggerErrorType } from '../../../../../data/logger'
import { SelectedFilter } from '../../../../../data/model/results'
import useDownloadSurveyDoc from '../../../../../hooks/useDownloadSurveyDoc'
import { useFieldwork } from '../../../../../hooks/useFieldwork'
import { getAllSummaryData } from '../../../../../hooks/useSummaryData'
import { useSurveyId } from '../../../../../hooks/useSurveyId'
import DuplicateProject from '../../../../../modules/Project/DuplicateProject'
import DialogEndProject from '../../../../../modules/Project/EndProject'
import PauseProject from '../../../../../modules/Project/PauseProject'
import {
  PROJECT_BY_SURVEY_ID,
  ProjectData
} from '../../../../../modules/Project/Project.query'
import RequestData from '../../../../../modules/Project/RequestData'
import RequestRawData from '../../../../../modules/Project/RequestRawData/RequestRawData'
import DialogExportToPowerPoint from '../../../../../modules/Results/DialogExportToPowerPoint/DialogExportToPowerPoint'
import EndMarket from '../../../../../modules/Survey/EndMarket'
import PauseMarket from '../../../../../modules/Survey/PauseMarket'
import {
  captureApolloError,
  handleShowPreview
} from '../../../../../utils/HelperFunctions'
import { convertAudienceType } from '../../../../../utils/audienceHelper'
import { downloadXlsxFile } from '../../../../../utils/chartUtils'
import { parseMarketName } from '../../../../../utils/marketFlags'
import { downloadPptxFile } from '../../../../../utils/pptxUtils'
import { isMultiMarketProjectType } from '../../../../../utils/projectUtils'
import ExportByLanguageDialog from '../../ActionsMenu/ExportByLanguageDialog'

const EXPORT_FAILED_MESSAGE =
  'The presentation failed to export, please retry - if it continues to fail, please contact support@focaldata.com'

interface Props {
  disableShowQuotas: boolean
  surveyState: SurveyState
  onShowQuotasChange: (showQuotas: boolean) => void
  selectedFilters: SelectedFilter[]
  // Language the results page is being viewed in, so exported chart data and
  // slides carry the same question/option text as the charts on screen.
  language?: string
  // Per-market results view of a MultiMarketV2 project: pause/end act on the
  // selected market's survey, the .doc export ships the shared (master)
  // questionnaire without audience questions, and downloads are named after
  // the project and the market. See the ResultsOptions control for the
  // visibility rules.
  multiMarketV2?: boolean
}

const ResultsOptions: React.FC<Props> = (props: Props) => {
  const surveyId = useSurveyId()
  const { useAnalyticsAPI } = useAnalyticsFeatureFlag()
  const {
    surveyState,
    disableShowQuotas = false,
    onShowQuotasChange,
    selectedFilters,
    language,
    multiMarketV2 = false
  } = props
  const [isDialogDataTablesOpen, setIsDialogDataTablesOpen] =
    useState<boolean>(false)
  const [isDialogRawDataOpen, setIsDialogRawDataOpen] = useState<boolean>(false)
  const [isDialogDuplicateOpen, setIsDialogDuplicateOpen] =
    useState<boolean>(false)
  const [isDialogPauseOpen, setIsDialogPauseOpen] = useState<boolean>(false)
  const [isEndDialogueOpen, setIsEndDialogueOpen] = useState<boolean>(false)
  const [isExportToPptxDialogOpen, setIsExportToPptxDialogOpen] =
    useState<boolean>(false)
  const [isExportingPptx, setIsExportingPptx] = useState<boolean>(false)
  const [isExportByLanguageOpen, setIsExportByLanguageOpen] =
    useState<boolean>(false)
  const { data: projectData } = useQuery<ProjectData>(PROJECT_BY_SURVEY_ID, {
    context: { clientName: 'dashboard' },
    variables: {
      surveyId
    },
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloQuery,
        'projectBySurveyId',
        error
      )
    }
  })

  const { exportSurvey } = useDownloadSurveyDoc()

  const { enqueueSnackbar } = useSnackbar()
  const { fieldwork } = useFieldwork()

  const selectedProject = projectData?.project
  const selectedSurvey = selectedProject?.surveys.find(
    (survey) => survey.surveyId === surveyId
  )
  const isMultiMarket =
    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    projectData && isMultiMarketProjectType(projectData?.project.projectType)

  // Chart/pptx downloads of a multi-market project are per market, so they are
  // named "<project> - <market>": the market name alone identifies neither the
  // project it came from (every project has a "Germany (DE)") nor, for the base
  // market, anything but the country. The country code is dropped (as every
  // other surface does), and so is the suffix for a master still named after
  // its project, which would otherwise repeat it.
  const marketName = multiMarketV2
    ? parseMarketName(selectedSurvey?.name ?? '').label
    : ''
  const downloadName =
    selectedProject && marketName && marketName !== selectedProject.name
      ? `${selectedProject.name} - ${marketName}`
      : selectedProject?.name

  const handleClickDownloadChartData: () => void = () => {
    getAllSummaryData(
      surveyId,
      selectedFilters,
      undefined,
      useAnalyticsAPI,
      language
    )
      .then((data) => {
        if (data?.results.length) {
          downloadXlsxFile({
            data: data.results,
            fileName: `${downloadName}.xlsx`
          })
        }
      })
      .catch((error) => {
        console.error('Error downloading chart data:', error)
      })
  }

  const openExportToPptxDialog = (): void => {
    setIsExportToPptxDialogOpen(true)
  }

  // The dialog closes only once a file has actually been written; every other
  // outcome leaves it open so the retry is one click away.
  const handleExportToPptx = async (): Promise<void> => {
    const fieldworkDates = selectedSurvey?.fieldworkDates

    if (!fieldworkDates) {
      enqueueSnackbar(EXPORT_FAILED_MESSAGE, { variant: SnackbarVariant.Error })
      datadogLogs.logger.error(
        `Export to PowerPoint found no project survey for: ${surveyId}`
      )
      return
    }

    setIsExportingPptx(true)
    try {
      const data = await getAllSummaryData(
        surveyId,
        selectedFilters,
        undefined,
        useAnalyticsAPI,
        language
      )

      // Undefined means fd-core-responses is still rehydrating the survey (202)
      // — the state the chart list shows its own banner for.
      if (!data) {
        enqueueSnackbar(
          'Your results are still being prepared. Try the export again in a few minutes.',
          { variant: SnackbarVariant.Warning }
        )
        return
      }

      if (!data.results.length) {
        enqueueSnackbar('There are no results to export yet.', {
          variant: SnackbarVariant.Warning
        })
        return
      }

      await downloadPptxFile(data.results, fieldworkDates, downloadName)
      setIsExportToPptxDialogOpen(false)
    } catch (error) {
      enqueueSnackbar(EXPORT_FAILED_MESSAGE, { variant: SnackbarVariant.Error })
      datadogLogs.logger.error(
        `Export to PowerPoint failed for survey: ${surveyId}`,
        { error }
      )
    } finally {
      setIsExportingPptx(false)
    }
  }
  const handleOnClickRawData: () => void = () => {
    setIsDialogRawDataOpen(!isDialogRawDataOpen)
  }
  const handleOnClickDataTables: () => void = () => {
    setIsDialogDataTablesOpen(!isDialogDataTablesOpen)
  }

  const handleDuplicate: () => void = () => {
    return setIsDialogDuplicateOpen(!isDialogDuplicateOpen)
  }
  const handleClickPause: () => void = () => {
    setIsDialogPauseOpen(!isDialogPauseOpen)
  }
  const handleClickEnd: () => void = () => {
    return setIsEndDialogueOpen(!isEndDialogueOpen)
  }
  const handleClickPreview: () => void = () => {
    const panelSupplierCode = convertAudienceType(
      fieldwork?.audience[0]?.__typename ?? 'CustomAudience'
    )
    handleShowPreview(surveyId, panelSupplierCode)
  }

  const handleExportSurvey: () => void = () => {
    // Multi-market: export the shared (master) questionnaire without the
    // market-specific audience questions.
    void exportSurvey(undefined, {
      includeAudienceQuestions: !multiMarketV2
    })
  }

  return (
    <>
      <ResultsOptionsControl
        onClickPause={handleClickPause}
        onClickEnd={handleClickEnd}
        onClickDuplicate={handleDuplicate}
        onClickPreview={handleClickPreview}
        onClickExportSurvey={handleExportSurvey}
        onClickExportByLanguage={
          isMultilangPocEnabled()
            ? () => setIsExportByLanguageOpen(true)
            : undefined
        }
        onClickChartData={handleClickDownloadChartData}
        onClickRawData={handleOnClickRawData}
        onClickDataTables={handleOnClickDataTables}
        onShowQuotasChange={onShowQuotasChange}
        onClickExportToPptx={openExportToPptxDialog}
        surveyState={surveyState}
        disableShowQuotas={disableShowQuotas}
        isMultiMarket={isMultiMarket}
        multiMarketV2={multiMarketV2}
      />
      <RequestData
        isDialogDataTablesOpen={isDialogDataTablesOpen}
        setIsDialogDataTablesOpen={setIsDialogDataTablesOpen}
      />
      <RequestRawData
        isDialogOpen={isDialogRawDataOpen}
        setIsDialogOpen={setIsDialogRawDataOpen}
      />
      <DuplicateProject
        isDialogDuplicateOpen={isDialogDuplicateOpen}
        setIsDialogDuplicateOpen={setIsDialogDuplicateOpen}
        selectedProject={selectedProject}
      />
      {/* PauseProject/EndProject act on `selectedProject.surveys[0]` — on a
          multi-market project that's not necessarily the market being viewed,
          so the per-market view pauses/ends the selected market's survey. */}
      {multiMarketV2 ? (
        <>
          <PauseMarket
            isOpen={isDialogPauseOpen}
            toggleIsOpen={setIsDialogPauseOpen}
            surveyId={surveyId}
            isPaused={surveyState === SurveyState.Paused}
          />
          <EndMarket
            isOpen={isEndDialogueOpen}
            toggleIsOpen={setIsEndDialogueOpen}
            surveyId={surveyId}
          />
        </>
      ) : (
        <>
          <PauseProject
            isOpen={isDialogPauseOpen}
            toggleIsOpen={setIsDialogPauseOpen}
            selectedProject={selectedProject}
          />
          <DialogEndProject
            isOpen={isEndDialogueOpen}
            toggleIsOpen={setIsEndDialogueOpen}
            selectedProject={selectedProject}
          />
        </>
      )}
      <DialogExportToPowerPoint
        isDialogOpen={isExportToPptxDialogOpen}
        isExporting={isExportingPptx}
        onExportToPptx={() => void handleExportToPptx()}
        onClose={() => setIsExportToPptxDialogOpen(false)}
      />
      {isMultilangPocEnabled() && (
        <ExportByLanguageDialog
          open={isExportByLanguageOpen}
          onClose={() => setIsExportByLanguageOpen(false)}
          defaultLanguage={language}
        />
      )}
    </>
  )
}

export default ResultsOptions
